import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Icon } from "@common/index";
import type { ProductImage } from "@models/product";

interface SortableImageTileProps {
    image: ProductImage;
    isPrimary: boolean;
    onSetPrimary: (imageId: string) => void;
    onRemove: (imageId: string) => void;
    disabled?: boolean;
}

// A single draggable thumbnail in the image manager. The whole tile is the
// drag handle (dnd-kit listeners); the star and remove controls stop pointer
// propagation so clicking them does not start a drag.
export const SortableImageTile = ({
    image,
    isPrimary,
    onSetPrimary,
    onRemove,
    disabled = false,
}: SortableImageTileProps) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: image.id, disabled });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="relative h-16 w-16 shrink-0 rounded bg-base-300 overflow-hidden touch-none"
            {...attributes}
            {...listeners}
        >
            <img
                src={image.url}
                alt="Product thumbnail"
                className="h-full w-full object-cover"
            />

            <button
                type="button"
                aria-label={isPrimary ? "Primary image" : "Set as primary"}
                aria-pressed={isPrimary}
                disabled={disabled}
                className="absolute top-0 left-0 p-0.5 text-warning"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={() => onSetPrimary(image.id)}
            >
                <Icon name="star" filled={isPrimary} className="text-base" />
            </button>

            <button
                type="button"
                aria-label="Remove image"
                disabled={disabled}
                className="absolute top-0 right-0 p-0.5 text-error"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={() => onRemove(image.id)}
            >
                <Icon name="close" className="text-base" />
            </button>
        </div>
    );
};
